export type SlaUrgency = 'none' | 'on_track' | 'due_soon' | 'overdue'

export type SlaStatusInfo = {
  urgency: SlaUrgency
  label: string
  toneLabel: string | null
  detail?: string
  progressPercent: number | null
}

export const slaToneStyles = {
  on_track: { scheme: 'green' as const, bg: 'green.50', color: 'green.700', border: 'green.200' },
  due_soon: { scheme: 'orange' as const, bg: 'orange.50', color: 'orange.700', border: 'orange.200' },
  overdue: { scheme: 'red' as const, bg: 'red.50', color: 'red.700', border: 'red.200' },
  none: { scheme: 'gray' as const, bg: 'gray.50', color: 'gray.600', border: 'gray.200' },
}

const DUE_SOON_MS = 24 * 60 * 60 * 1000

export function getSlaStatus(slaDueAt: string | null | undefined, now = Date.now()): SlaStatusInfo {
  if (!slaDueAt) {
    return { urgency: 'none', label: 'No SLA target', toneLabel: null, progressPercent: null }
  }

  const due = new Date(slaDueAt).getTime()
  if (Number.isNaN(due)) {
    return { urgency: 'none', label: 'SLA unavailable', toneLabel: null, progressPercent: null }
  }

  const remaining = due - now
  const detail = new Date(slaDueAt).toLocaleString('en-PH', {
    timeZone: 'Asia/Manila',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })

  if (remaining <= 0) {
    const overdueMs = Math.abs(remaining)
    return {
      urgency: 'overdue',
      label: formatRelativeDuration(overdueMs, 'overdue'),
      toneLabel: 'Overdue',
      detail: `Breached ${formatBreachedAgo(overdueMs)}`,
      progressPercent: 100,
    }
  }

  if (remaining <= DUE_SOON_MS) {
    return {
      urgency: 'due_soon',
      label: formatRelativeDuration(remaining, 'due'),
      toneLabel: 'Due soon',
      detail: formatRelativeDuration(remaining, 'due'),
      progressPercent: Math.min(99, Math.round((1 - remaining / DUE_SOON_MS) * 100)),
    }
  }

  return {
    urgency: 'on_track',
    label: formatRelativeDuration(remaining, 'due'),
    toneLabel: 'On track',
    detail: formatRelativeDuration(remaining, 'due'),
    progressPercent: Math.max(5, Math.min(85, 50)),
  }
}

export function getSlaTone(urgency: SlaUrgency) {
  return slaToneStyles[urgency === 'none' ? 'none' : urgency]
}

function formatBreachedAgo(ms: number): string {
  const minutes = Math.floor(ms / 60000)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (days >= 1) {
    const remH = hours % 24
    return `${days}d${remH > 0 ? ` ${remH}h` : ''} ago`
  }
  if (hours >= 1) {
    const remM = minutes % 60
    return `${hours}h${remM > 0 ? ` ${remM}m` : ''} ago`
  }
  return `${Math.max(1, minutes)}m ago`
}

function formatRelativeDuration(ms: number, mode: 'due' | 'overdue'): string {
  const abs = Math.abs(ms)
  const minutes = Math.floor(abs / 60000)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (days >= 1) {
    const remH = hours % 24
    const unit = `${days}d${remH > 0 ? ` ${remH}h` : ''}`
    return mode === 'overdue' ? `Overdue by ${unit}` : `Due in ${unit}`
  }
  if (hours >= 1) {
    const remM = minutes % 60
    const unit = `${hours}h${remM > 0 ? ` ${remM}m` : ''}`
    return mode === 'overdue' ? `Overdue by ${unit}` : `Due in ${unit}`
  }
  const unit = `${Math.max(1, minutes)}m`
  return mode === 'overdue' ? `Overdue by ${unit}` : `Due in ${unit}`
}
